import React, {useEffect, useRef, useState} from 'react';
import Card from "../../../components/card";
import {AgGridReact} from "ag-grid-react";
import {LocalizationAgGrid} from "../../../utils/localization-ag-grid";
import TemplateDefault from "../../../components/template-default";
import SelectComponent from "../../../components/select-component";
import InputDateTime from "../../../components/input-datetime";
import {FaList, FaPrint} from "react-icons/fa";
import Button from "../../../components/button";
import reportService from "../../../services/report-service";
import {useQuery} from "@tanstack/react-query";
import projectService from "../../../services/project-service";
import {toast} from "react-toastify";
import {AlertMessage} from "../../../utils/alert-message";
import moment from "moment";
import {print} from "../components/print";
import "../report.css";
import general from "../../../utils/general";
import RequestRender from "../components/request-render";

const BASENAME = process.env.PUBLIC_URL;

const initialFilters = {
    status: {value: "Todos", label: "Todos"},
    init: moment().startOf('month').toDate(),
    end: moment().endOf('month').toDate(),
    project: ""
}

const SoftwareAttendanceReport = () => {
    const [data, setData] = useState([]);
    const [filters, setFilters] = useState(initialFilters);
    const [filtersStr, setFiltersStr] = useState("");
    const handleSelect = (id, value) => setFilters(prev => ({...prev, [id]: value}));
    const handleInput = (value, name) => setFilters(prev => ({...prev, [name]: value}));
    const onCellClicked = (cell) => window.open(`${BASENAME}/software-attendances/form/${cell.data.id}`);
    const [pinnedBottomValues, setPinnedBottomValues] = useState({ observation: "TOTAL DE ATENDIMENTOS: 0" });

    useEffect(() => {
        document.title = "SISTEMA INTERNO";
    }, []);

    const [columnDefs] = useState([
        {field: "dt_initial", headerName: "Abertura", width: 120,
            cellRenderer: params => {
                return general.formatDate(params.value)
            }
        },
        {field: "requester", headerName: "Solicitante", width: 150,
            cellRenderer: RequestRender,
            cellRendererParams: {
                data: data,
                url: "/software-attendances/form/"
            },
        },
        {field: "problem", headerName: "Requisição", wrapText: true, autoHeight: true, flex: 1, minWidth: 250},
        {field: "observation", headerName: "Observação", wrapText: true, autoHeight: true, flex: 1, minWidth: 250},
    ]);

    const {data: projects} = useQuery({
        queryFn: () => projectService.options().then(({data}) => data),
        queryKey: ["projects"]
    });

    const gridRef = useRef();

    const formatDate = date => {
        if (date) return date.toLocaleDateString() + " " + date.toLocaleTimeString();

        return "";
    }

    useEffect(() => {
        setPinnedBottomValues({observation: `TOTAL DE ATENDIMENTOS: ${data.length}`})
    }, [data]);

    const apply = () => {
        const init = formatDate(filters.init);
        const end = formatDate(filters.end);
        const formData = new FormData();

        formData.append("status", filters.status?.value ?? "");
        formData.append("init", init);
        formData.append("end", end);
        formData.append("project", filters.project?.value ?? "");

        const filtersStr = `Início: ${init} - Fim: ${end} - Status: ${filters.status.value}`;
        setFiltersStr(filtersStr);

        reportService.softwareAttendance(formData)
            .then(({data}) => setData(data))
            .catch((err) => {
                const {data: {message}} = err.response;
                toast.error(AlertMessage[message] ?? "Não foi possível prosseguir.");
            });
    }

    const handlePrint = () => {
        if (gridRef.current) {
            const title = `RELATÓRIO DE ATENDIMENTOS - ${filters.project.label}`;
            const footer = `<tr><td colspan='3'></td><td>${pinnedBottomValues.observation}</td></tr>`
            print(gridRef, columnDefs, title, filtersStr, footer);
        }
    }

    return (
        <TemplateDefault title="Relatório de atendimentos">
            <Card>
                <div className="flex flex-col lg:grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-12">
                    <div className="col-span-2 text-gray-900">
                        <SelectComponent
                            instanceId="status"
                            label="Status"
                            options={[
                                {value: "Todos", label: "Todos"},
                                {value: "Aberto", label: "Aberto"},
                                {value: "Encerrado", label: "Encerrado"},
                            ]}
                            value={filters.status}
                            onChange={(value) => handleSelect("status", value)}
                            name="status"
                        />
                    </div>
                    <div className={"col-span-2"}>
                        <InputDateTime id={"date-init"} name={"init"} label={"Inicio"} selected={filters.init}
                                       onChange={(value) => handleInput(value, "init")} showTime/>
                    </div>
                    <div className={"col-span-2"}>
                        <InputDateTime id={"date-end"} name={"end"} label={"Fim"} selected={filters.end}
                                       onChange={(value) => handleInput(value, "end")} showTime/>
                    </div>

                    <div className="col-span-4 text-gray-900">
                        <SelectComponent
                            instanceId="projects"
                            label="Projeto"
                            options={projects ?? []}
                            value={filters.project}
                            onChange={(value) => handleSelect("project", value)}
                            name="projects"
                        />
                    </div>
                    <Button col={"col-span-1"} onClick={apply}>
                        <FaList/> <span className={"ml-1"}>Aplicar</span>
                    </Button>
                    <Button col={"col-span-1"} onClick={handlePrint}>
                        <FaPrint/>
                    </Button>

                </div>
                <div className={"ag-theme-quartz grid mt-4 report-table"} style={{height: "calc(100vh - 159px)"}}>
                    <AgGridReact
                        ref={gridRef}
                        rowHeight={32}
                        rowData={data}
                        columnDefs={columnDefs}
                        animateRows={false}
                        suppressRowClickSelection={true}
                        suppressCellFocus={true}
                        onCellClicked={onCellClicked}
                        gridOptions={{localeText: LocalizationAgGrid}}
                        domLayout='autoHeight'
                        pinnedBottomRowData={[pinnedBottomValues]}
                        getRowStyle={(params) => {
                            if (params.node.rowPinned) {
                                return { pointerEvents: "none", fontWeight: "700"};
                            }
                        }}
                    />
                </div>
            </Card>
        </TemplateDefault>
    );
};

export default SoftwareAttendanceReport;